import axios from "axios";

// Use backend API instead of calling HubSpot directly (avoids CORS and keeps PAT secure)
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3005";

const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Search for a HubSpot contact by email
 * @param {string} email - The email to search for
 * @returns {Promise<object|null>} - The contact object or null
 */
export async function searchContactByEmail(email) {
  if (!email) {
    throw new Error("Email is required to search contact.");
  }

  const { data } = await apiClient.post("/hubspot/contact/search", { email });

  if (data?.result === "error") {
    throw new Error(data?.message || "Failed to search contact");
  }

  return data?.contact ?? null;
}

/**
 * Get visit data by contact ID
 * @param {string} contactId - The HubSpot contact ID
 * @param {string} projectId - The project ID (optional)
 * @returns {Promise<object|null>} - The visit object or null
 */
export async function getVisitByContactId(contactId, projectId) {
  if (!contactId) {
    throw new Error("contactId is required to fetch visit data.");
  }

  const payload = { contactId };
  if (projectId) {
    payload.projectId = projectId;
  }

  const { data } = await apiClient.post("/hubspot/visit/search", payload);

  if (data?.result === "error") {
    throw new Error(data?.message || "Failed to fetch visit");
  }

  return data?.visit ?? null;
}

/**
 * Update a visit property by contact ID
 * @param {object} params - { contactId, key, value, projectId }
 * @returns {Promise<boolean>} - true if successful
 */
export async function patchVisitProperty({ contactId, key, value, projectId }) {
  if (!contactId) {
    throw new Error("contactId is required to update visit.");
  }
  if (!key) {
    throw new Error("key is required to update visit.");
  }

  const payload = { contactId, key, value };
  if (projectId) {
    payload.projectId = projectId;
  }

  const { data } = await apiClient.patch("/hubspot/visit/update", payload);

  if (data?.result === "error") {
    throw new Error(data?.message || "Failed to update visit");
  }

  return true;
}

/**
 * Update a visit property by email (convenience function)
 * @param {object} params - { email, key, value, projectId }
 * @returns {Promise<boolean|null>} - true if successful, null if not found
 */
export async function patchVisitByEmail({ email, key, value, projectId }) {
  if (!email) {
    throw new Error("email is required.");
  }
  if (!key) {
    throw new Error("key is required.");
  }

  const payload = { email, key, value };
  if (projectId) {
    payload.projectId = projectId;
  }

  try {
    const { data } = await apiClient.patch("/hubspot/visit/update-by-email", payload);

    if (data?.result === "error") {
      if (data?.message?.includes("not found")) {
        return null;
      }
      throw new Error(data?.message || "Failed to update visit");
    }

    return true;
  } catch (err) {
    if (err?.response?.status === 404) {
      return null;
    }
    throw err;
  }
}
